import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext.jsx';
import { apiClient } from '../../../lib/apiClient.js';

function fmt(n) {
  return Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function Field({ label, value, empty = '—', fullWidth = false }) {
  return (
    <div className={fullWidth ? 'col-span-2' : ''}>
      <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-0.5">{label}</p>
      <p className="text-sm text-text">{value ?? empty}</p>
    </div>
  );
}

function StatCard({ label, value, sub, color = 'text-text' }) {
  return (
    <div className="card p-4">
      <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-1">{label}</p>
      <p className={`text-[22px] font-bold tabular-nums leading-tight ${color}`}>{value}</p>
      {sub && <p className="text-[11px] text-text-faint mt-0.5">{sub}</p>}
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-bg-s2">
        <h2 className="text-[13px] font-semibold text-text">{title}</h2>
        {action}
      </div>
      <div className="p-5 grid grid-cols-2 gap-x-6 gap-y-4">
        {children}
      </div>
    </div>
  );
}

export default function ParticipantProfile() {
  const { principal } = useAuth();
  const [participant, setParticipant] = useState(null);
  const [profile, setProfile] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [participantsRes, profileRes, plansRes] = await Promise.all([
        apiClient.listParticipants(),
        apiClient.getParticipantProfile(),
        apiClient.listPlans(),
      ]);

      const list = participantsRes?.participants ?? [];
      const found = list.find((p) => p.participant_id === principal?.participantId);
      setParticipant(found || null);
      setProfile(profileRes || null);

      const plans = plansRes?.plans ?? [];
      const myPlan = plans.find((p) => p.plan_id === found?.plan_id);
      setPlan(myPlan || null);
    } catch (e) {
      setError(e.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [principal?.participantId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {[1,2,3].map((i) => <div key={i} className="card p-4 h-20 animate-pulse bg-bg-s2" />)}
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} className="card p-5 space-y-3 animate-pulse">
            <div className="h-3 w-32 bg-bg-s3 rounded" />
            <div className="grid grid-cols-2 gap-4">
              {[1,2,3,4].map((j) => <div key={j} className="h-8 bg-bg-s3 rounded" />)}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card p-5 border-danger/30 bg-danger/5">
          <p className="text-sm text-danger">{error}</p>
        </div>
      </div>
    );
  }

  const address = profile?.address || {};
  const financials = profile?.financials || {};
  const primaryBeneficiary = profile?.beneficiaries?.primary?.[0] || null;

  const vestingLabel = participant?.vesting_pct != null
    ? `${Math.round(participant.vesting_pct * 100)}%`
    : '—';
  const yearsLabel = participant?.years_of_service != null
    ? `${participant.years_of_service} yr${participant.years_of_service !== 1 ? 's' : ''}`
    : '—';
  const deferralLabel = financials.deferral_pct != null
    ? `${(financials.deferral_pct * 100).toFixed(1).replace(/\.0$/, '')}%`
    : '—';
  const deferralTypeLabel = financials.deferral_type === 'roth' ? 'Roth (after-tax)' :
    financials.deferral_type === 'pre_tax' ? 'Pre-Tax (traditional)' : '—';

  return (
    <div className="max-w-2xl mx-auto space-y-4">

      {/* Header */}
      <div className="card overflow-hidden flex">
        <div className="w-1 bg-accent flex-shrink-0" />
        <div className="p-5 flex-1">
          <h1 className="text-[20px] font-bold text-text leading-tight">My Profile</h1>
          <p className="text-sm text-text-muted mt-1">Account information and plan details. Use dedicated pages to make changes.</p>
        </div>
      </div>

      {/* Balance stat cards */}
      <div className="grid grid-cols-3 gap-3">
        <StatCard
          label="Total Balance"
          value={financials.total_balance != null ? fmt(financials.total_balance) : '—'}
          sub="All contributions"
          color="text-text"
        />
        <StatCard
          label="Vested Balance"
          value={financials.vested_balance != null ? fmt(financials.vested_balance) : '—'}
          sub="Available to withdraw"
          color="text-success"
        />
        <StatCard
          label="Loan Headroom"
          value={participant?.loan_headroom != null ? fmt(participant.loan_headroom) : '—'}
          sub="Max new loan"
          color="text-text"
        />
      </div>

      {/* Account */}
      <Section title="Account">
        <Field label="Participant ID" value={participant?.participant_id} />
        <Field label="Plan" value={plan?.plan_name || participant?.plan_id} />
        <Field label="Employment Status" value={
          participant?.employment_status
            ? participant.employment_status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
            : null
        } />
        <Field label="HCE Status" value={participant?.is_hce ? 'Highly Compensated Employee' : 'Non-HCE'} />
      </Section>

      {/* Contributions */}
      <Section
        title="Contributions"
        action={
          <Link to="/participant/contribution-change" className="text-[11px] text-accent hover:text-accent-dark font-medium transition-colors">
            Change →
          </Link>
        }
      >
        <Field label="Deferral Rate" value={deferralLabel} />
        <Field label="Deferral Type" value={deferralTypeLabel} />
        <Field label="Catch-Up Eligible" value={participant?.age_60_to_63 ? 'Yes (ages 60–63, SECURE 2.0)' : participant?.catch_up_eligible ? 'Yes (age 50+)' : 'No'} />
        <Field label="Annual Deferral Limit" value={participant?.age_60_to_63 ? '$34,250' : participant?.catch_up_eligible ? '$30,500' : '$23,000'} />
      </Section>

      {/* Vesting */}
      <Section title="Vesting & Service">
        <Field label="Years of Service" value={yearsLabel} />
        <Field label="Vesting Percentage" value={vestingLabel} />
        <Field label="Vested Balance" value={financials.vested_balance != null ? fmt(financials.vested_balance) : null} />
        <Field label="Outstanding Loans" value={participant?.outstanding_loans != null ? `${participant.outstanding_loans}` : '0'} />
      </Section>

      {/* Address */}
      <Section
        title="Mailing Address"
        action={
          <Link to="/participant/beneficiary-update" className="text-[11px] text-accent hover:text-accent-dark font-medium transition-colors">
            Edit →
          </Link>
        }
      >
        <div className="col-span-2">
          {address.street_address ? (
            <p className="text-sm text-text">
              {address.street_address}<br />
              {address.city}, {address.state} {address.zip_code}
            </p>
          ) : (
            <p className="text-sm text-text-faint">No address on file.</p>
          )}
        </div>
      </Section>

      {/* Beneficiary */}
      <Section
        title="Primary Beneficiary"
        action={
          <Link to="/participant/beneficiary-update" className="text-[11px] text-accent hover:text-accent-dark font-medium transition-colors">
            Edit →
          </Link>
        }
      >
        {primaryBeneficiary ? (
          <>
            <Field label="Name" value={primaryBeneficiary.name} />
            <Field label="Relationship" value={primaryBeneficiary.relationship} />
            <Field label="Date of Birth" value={primaryBeneficiary.dob} />
            <Field label="Share" value={primaryBeneficiary.share_pct != null ? `${primaryBeneficiary.share_pct}%` : null} />
          </>
        ) : (
          <div className="col-span-2">
            <p className="text-sm text-text-faint">
              No beneficiary on file.{' '}
              <Link to="/participant/beneficiary-update" className="text-accent hover:underline">Add one →</Link>
            </p>
          </div>
        )}
      </Section>

      <p className="text-[11px] text-text-faint text-center font-mono pb-2">
        Balances reflect your current plan record · ERISA §107 · IRC §401(k)
      </p>
    </div>
  );
}
